import React from 'react';
import { Music } from 'lucide-react';

/**
 * Render a 6-string guitar tablature with proper color hierarchy.
 *
 * Tab lines look like:
 *   e|------------------------1-2-3-4-|
 *   B|------------------1-2-3-4-------|
 *   ...
 *
 * Anything that doesn't start with a single-letter string label + pipe is
 * passed through as a plain monospace caption (used for descriptive text
 * the lesson data sometimes mixes in).
 */
const STRING_LABELS = new Set(['e', 'B', 'G', 'D', 'A', 'E']);
const STRING_COLORS = {
  e: 'text-cyan-300',
  B: 'text-purple-300',
  G: 'text-pink-300',
  D: 'text-amber',
  A: 'text-orange-400',
  E: 'text-red-400',
};

const renderTabBody = (body) => {
  // Split on numeric chunks while preserving them, so 12-fret numbers
  // stay together. Result alternates: text, number, text, number, ...
  // Each digit MUST occupy exactly 1ch in the monospace font so the
  // bar lines stay aligned across all six string lines. Padding /
  // margin on a styled fret number would push the rest of the line
  // and break the column grid the etudes rely on.
  const parts = body.split(/(\d+)/);
  return parts.map((p, i) => {
    if (/^\d+$/.test(p)) {
      return (
        <span
          key={i}
          className="text-amber font-bold bg-amber/15 rounded-sm"
          style={{
            display: 'inline-block',
            width: `${p.length}ch`,
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {p}
        </span>
      );
    }
    // Plain dashes/separators
    return (
      <span key={i} className="text-zinc-600">
        {p}
      </span>
    );
  });
};

const TabNotation = ({ children, title = 'Tab' }) => {
  if (!children) return null;
  const raw = String(children).trimEnd();
  const lines = raw.split('\n');

  return (
    <figure className="my-2">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-amber">
          <div className="w-7 h-7 rounded-lg bg-amber/15 border border-amber/30 flex items-center justify-center">
            <Music className="w-3.5 h-3.5" />
          </div>
          <span className="font-heading font-bold uppercase tracking-[0.18em] text-xs">
            {title}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono">
          E A D G B e
        </span>
      </header>

      <div className="relative rounded-2xl bg-gradient-to-br from-[#0A0A0A] to-[#0E0E10] border border-amber/15 overflow-hidden">
        {/* subtle ruled background */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent 0, transparent 23px, #FF9F1C 23px, #FF9F1C 24px)',
          }}
        />
        <pre className="relative font-mono text-[13px] sm:text-sm leading-6 p-5 overflow-x-auto whitespace-pre">
          {lines.map((line, idx) => {
            // Detect a tab string line: <label>|<rest>
            const m = line.match(/^([eBGDAE])\|(.*)$/);
            if (m) {
              const [, label, body] = m;
              const colour = STRING_COLORS[label] || 'text-zinc-300';
              return (
                <div key={idx} className="flex items-center">
                  <span className={`${colour} font-bold w-5 shrink-0`}>{label}</span>
                  <span className="text-zinc-500 px-1">|</span>
                  <span className="flex-1 tracking-tight">{renderTabBody(body)}</span>
                </div>
              );
            }
            // Caption / description lines
            if (line.trim() === '') {
              return <div key={idx} className="h-3" />;
            }
            return (
              <div key={idx} className="text-zinc-400 italic text-xs mt-3 not-italic">
                {line}
              </div>
            );
          })}
        </pre>
      </div>
    </figure>
  );
};

export default TabNotation;
